import { Delete, Info, MoreVert } from '@mui/icons-material';
import {
    Alert,
    Box,
    FormControlLabel,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { useAvailableLocales } from 'src/hooks/useAvailableLocales';
import { useProductVersions } from 'src/hooks/useProductVersions';
import { NumericInput } from 'src/components/SetsPlayground/NumericInputField';
import { DISTRIBUTION_HELP_TEXT, DistributionWidgetContainer } from 'src/components/SetsPlayground/Widgets/DistributionWidgetContainer';
import { Collapsable } from 'src/components/common/Collapsable/Collapsable';
import { CollapsableToggle } from 'src/components/common/Collapsable/CollapsableToggle';
import {
    ChangeEvent, MouseEvent, useEffect, useState,
} from 'react';
import { AddSetMenu } from './AddSetMenu';
import { CulturePicker } from './CulturePicker';
import { calculateAllocatedShare } from './distributionTotal';
import { DEFAULT_NEWNESS_DISTRIBUTION, isReservedSetName } from './newnessSet';
import { SetEditorCard } from './SetEditorCard';
import { getNewnessDistributionValidationErrors, getSetValidationErrors } from './setValidation';

type SetsConfigCultureGroup = Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup;
type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetDistribution = Models.SetsPlayground.SetDistribution;

const PanelContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4, 0),
}));

const ChildContent = styled(Box)(({ theme }) => ({
    borderLeft: `2px solid ${theme.palette.grey[300]}`,
    paddingLeft: theme.spacing(3),
    marginLeft: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
    },
}));

// Short controls sit side by side rather than stacked, so they fill the content column instead
// of leaving a dead gutter beside them.
const FieldRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    '& > *:not(button)': {
        flex: '1 1 260px',
    },
    '& > button': {
        flex: '0 0 auto',
    },
}));

const DateRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
}));

const NewnessSetCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderLeft: `${theme.spacing(1)} solid ${theme.palette.grey[500]}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
}));

const NewnessSetHeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const NewnessSetHeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const NewnessSetDetails = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    paddingTop: theme.spacing(3),
}));

const NewnessDistributionSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

interface CultureGroupPanelProps {
    group: SetsConfigCultureGroup;
    groupIndex: number;
    cultureGroups: SetsConfigCultureGroup[];
    productKey: string;
    duplicateNameIndexes: Set<number>;
    emptyCultureIndexes: Set<number>;
    onUpdateName: (groupIndex: number, name: string) => void;
    onUpdateCultures: (groupIndex: number, cultures: string[]) => void;
    onUpdateDates: (groupIndex: number, startDate: string | null, endDate: string | null) => void;
    onToggleNewness: (groupIndex: number, enabled: boolean) => void;
    onUpdateNewnessLookbackDays: (groupIndex: number, days: number | null) => void;
    onUpdateNewnessDistribution: (groupIndex: number, distribution: SetDistribution) => void;
    onUpdateNotes: (groupIndex: number, notes: string) => void;
    onAddSet: (groupIndex: number) => void;
    onUpdateSet: (groupIndex: number, setIndex: number, update: Partial<SetDefinition>) => void;
    onRemoveSet: (groupIndex: number, setIndex: number) => void;
    onRemoveGroup: (groupIndex: number) => void;
}

export const CultureGroupPanel = ({
    group,
    groupIndex,
    cultureGroups,
    productKey,
    duplicateNameIndexes,
    emptyCultureIndexes,
    onUpdateName,
    onUpdateCultures,
    onUpdateDates,
    onToggleNewness,
    onUpdateNewnessLookbackDays,
    onUpdateNewnessDistribution,
    onUpdateNotes,
    onAddSet,
    onUpdateSet,
    onRemoveSet,
    onRemoveGroup,
}: CultureGroupPanelProps): JSX.Element => {
    const trimmedName = group.name.trim();
    const isEmptyName = trimmedName === '';
    const isDuplicateName = duplicateNameIndexes.has(groupIndex);
    const nameHasError = isEmptyName || isDuplicateName;
    const cultureHasError = emptyCultureIndexes.has(groupIndex);
    const hasCultures = group.cultures.length > 0;

    const [newnessExpanded, setNewnessExpanded] = useState(true);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setNewnessExpanded(true);
    }, [groupIndex]);

    const handleMenuOpen = (event: MouseEvent<HTMLElement>): void => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (): void => {
        setMenuAnchorEl(null);
    };

    const handleDeleteGroup = (): void => {
        handleMenuClose();
        onRemoveGroup(groupIndex);
    };

    const { currentProductVersion } = useProductVersions(productKey);
    const { availableLocales } = useAvailableLocales(
        productKey,
        currentProductVersion ? parseInt(currentProductVersion, 10) : null,
    );

    let nameHelperText = ' ';

    if (isEmptyName) {
        nameHelperText = 'Group name is required';
    } else if (isDuplicateName) {
        nameHelperText = 'Group name must be unique';
    }

    const duplicateSetNameIndexes = new Set<number>();
    const seenSetNames = new Map<string, number>();

    group.sets.forEach((set, index) => {
        const name = set.name.trim();

        if (!name) return;

        const previousIndex = seenSetNames.get(name);

        if (previousIndex !== undefined) {
            duplicateSetNameIndexes.add(previousIndex);
            duplicateSetNameIndexes.add(index);
        } else {
            seenSetNames.set(name, index);
        }
    });

    const reservedSetNameIndexes = new Set<number>();

    group.sets.forEach((set, index) => {
        if (isReservedSetName(set.name)) {
            reservedSetNameIndexes.add(index);
        }
    });

    const newnessValidationErrors = group.newnessEnabled
        ? getNewnessDistributionValidationErrors(group.newnessDistribution)
        : [];

    const allocatedPercent = Math.round(calculateAllocatedShare({
        sets: group.sets,
        newnessEnabled: group.newnessEnabled,
        newnessDistribution: group.newnessDistribution,
    }) * 100);
    const isOverAllocated = allocatedPercent > 100;
    const remainingPercent = Math.max(0, 100 - allocatedPercent);

    return (
        <PanelContainer>
            <ChildContent>
                <FieldRow>
                    <TextField
                        required
                        error={nameHasError}
                        helperText={nameHelperText}
                        label="Group name"
                        size="small"
                        value={group.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>): void => onUpdateName(groupIndex, e.target.value)}
                    />
                    <CulturePicker
                        availableCultures={availableLocales}
                        cultureGroups={cultureGroups}
                        cultures={group.cultures}
                        error={cultureHasError}
                        groupIndex={groupIndex}
                        helperText={cultureHasError ? 'Culture group must have at least one culture' : ' '}
                        onChange={(cultures): void => onUpdateCultures(groupIndex, cultures)}
                    />
                    <IconButton aria-label="culture group actions" onClick={handleMenuOpen}>
                        <MoreVert />
                    </IconButton>
                </FieldRow>
                <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleMenuClose}>
                    <MenuItem onClick={handleDeleteGroup}>
                        <ListItemIcon>
                            <Delete color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ color: 'error' }}>
                            Delete culture group
                        </ListItemText>
                    </MenuItem>
                </Menu>
                <TextField
                    multiline
                    label="Group notes"
                    minRows={2}
                    value={group.notes}
                    onChange={(e: ChangeEvent<HTMLInputElement>): void => onUpdateNotes(groupIndex, e.target.value)}
                />
                <Box>
                    <Typography gutterBottom sx={{ display: 'flex', alignItems: 'center' }} variant="subtitle2">
                        Active window
                        <Tooltip title="Leave both dates empty to keep this culture group always active.">
                            <Info sx={{
                                ml: 1, height: 16, width: 16, color: 'primary.main',
                            }}
                            />
                        </Tooltip>
                    </Typography>
                    <DateRow>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            label="Start date"
                            size="small"
                            type="date"
                            value={group.startDate ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>): void => (
                                onUpdateDates(groupIndex, e.target.value || null, group.endDate)
                            )}
                        />
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            label="End date"
                            size="small"
                            type="date"
                            value={group.endDate ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>): void => (
                                onUpdateDates(groupIndex, group.startDate, e.target.value || null)
                            )}
                        />
                    </DateRow>
                </Box>
                <Box alignItems="center" display="flex" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography sx={{ fontWeight: 'fontWeightMedium' }} variant="subtitle1">
                        Sets
                    </Typography>
                    <AddSetMenu disabled={!hasCultures} onAdd={(): void => onAddSet(groupIndex)} />
                </Box>
                <Typography color="text.secondary" variant="body2">
                    {`${allocatedPercent}% of positions allocated, ${remainingPercent}% remaining`}
                    {' · Explicit positions not included'}
                </Typography>
                {isOverAllocated && (
                    <Alert severity="warning">
                        {`Sets ask for ${allocatedPercent}% of positions, more than the gallery has. Higher `
                            + 'priority sets fill first, so lower priority sets will get less than configured.'}
                    </Alert>
                )}
                <NewnessSetCard>
                    <NewnessSetHeaderRow>
                        <Box>
                            <Typography
                                sx={{ display: 'flex', alignItems: 'center', fontWeight: 'fontWeightMedium' }}
                                variant="body1"
                            >
                                Newness Set
                                <Tooltip title="Adds designs created within the lookback period. It is always prioritized before the other sets. The distribution controls how many tiles it gets and where.">
                                    <Info sx={{
                                        ml: 1, height: 16, width: 16, color: 'primary.main',
                                    }}
                                    />
                                </Tooltip>
                            </Typography>
                            <Typography color="text.secondary" variant="body2">
                                Toggle whether new content is included for this culture group.
                            </Typography>
                        </Box>
                        <Box sx={{ maxWidth: 260 }}>
                            <NumericInput
                                showStepper
                                disabled={!group.newnessEnabled}
                                label="Lookback period (days)"
                                min={0}
                                value={group.newnessLookbackDays ?? undefined}
                                onChange={(days): void => onUpdateNewnessLookbackDays(groupIndex, days ?? null)}
                            />
                        </Box>
                        <NewnessSetHeaderActions>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={group.newnessEnabled}
                                        onChange={(e): void => onToggleNewness(groupIndex, e.target.checked)}
                                    />
                                    )}
                                label={group.newnessEnabled ? 'On' : 'Off'}
                                labelPlacement="start"
                            />
                            <CollapsableToggle
                                aria-label={newnessExpanded ? 'Collapse newness set details' : 'Expand newness set details'}
                                expanded={newnessExpanded}
                                size="small"
                                onClick={(_event, isExpanded): void => setNewnessExpanded(isExpanded)}
                            />
                        </NewnessSetHeaderActions>
                    </NewnessSetHeaderRow>
                    <Collapsable expanded={newnessExpanded}>
                        <NewnessSetDetails>
                            <NewnessDistributionSection>
                                <Typography
                                    sx={{ display: 'flex', alignItems: 'center', fontWeight: 'fontWeightMedium' }}
                                    variant="body1"
                                >
                                    Distribution
                                    <Tooltip title={DISTRIBUTION_HELP_TEXT}>
                                        <Info sx={{
                                            ml: 1, height: 16, width: 16, color: 'primary.main',
                                        }}
                                        />
                                    </Tooltip>
                                </Typography>
                                <DistributionWidgetContainer
                                    disabled={!group.newnessEnabled}
                                    index={0}
                                    set={{
                                        distribution: group.newnessDistribution ?? DEFAULT_NEWNESS_DISTRIBUTION,
                                    } as SetDefinition}
                                    onChangeDistribution={(_index, distribution): void => (
                                        onUpdateNewnessDistribution(groupIndex, distribution)
                                    )}
                                />
                                {newnessValidationErrors.length > 0 && (
                                    <Alert severity="error">
                                        {newnessValidationErrors.map((message) => (
                                            <div key={message}>{message}</div>
                                        ))}
                                    </Alert>
                                )}
                            </NewnessDistributionSection>
                        </NewnessSetDetails>
                    </Collapsable>
                </NewnessSetCard>
                {group.sets.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                        No sets configured for this group yet.
                    </Typography>
                ) : (
                    group.sets.map((set, setIndex) => (
                        <SetEditorCard
                            cultures={group.cultures}
                            displayIndex={setIndex}
                            hasDuplicateName={duplicateSetNameIndexes.has(setIndex)}
                            hasReservedName={reservedSetNameIndexes.has(setIndex)}
                                // eslint-disable-next-line react/no-array-index-key
                            key={`set-${setIndex}`}
                            productKey={productKey}
                            set={set}
                            validationErrors={getSetValidationErrors(set)}
                            onChange={(update): void => onUpdateSet(groupIndex, setIndex, update)}
                            onRemove={(): void => onRemoveSet(groupIndex, setIndex)}
                        />
                    ))
                )}
            </ChildContent>
        </PanelContainer>
    );
};
